import React, { useEffect, useState } from "react";
import { Box, Icon, Table, Text, Dropdown, Spinner } from "~/components-shared";
import styled, { useTheme } from "styled-components";
import moment from "moment";
import { usePostHog } from "posthog-js/react";
import { AuthoritiesEnum, StatusStateEnum } from "~/types/enums";
import lists from "~/lib/lists";
import Markdown from "~/components/Markdown";
import { useTranslation } from "react-i18next";
import useAbacExt from "~/hooks/abac";
import insightsActionCreators from "~/store/insights/actions";
import toastsActionCreators from "~/store/toasts/actions";
import { useDispatch, useSelector } from "react-redux";
import insightsSelectors from "~/store/insights/selectors";
const ImpactIcon = styled.span`
  transform: rotate(45deg);
  width: 0.7rem;
  height: 0.7rem;
  background-color: ${_ref => {
  let {
    color
  } = _ref;
  return color;
}};
  display: inline-block;
  margin-right: 0.5rem;
`;
const WhatElseTableRow = _ref2 => {
  let {
    showDismissed,
    isEditable = false,
    item
  } = _ref2;
  const {
    t
  } = useTranslation();
  const [itemState, setItemState] = useState(item);
  const dismissStatus = useSelector(insightsSelectors.getDismissStatusByLineItem(item.id));
  const deleteStatus = useSelector(insightsSelectors.getDeleteStatusByLineItem(item.id));
  const dismissError = useSelector(insightsSelectors.getDismissErrorByLineItem(item.id));
  const deleteError = useSelector(insightsSelectors.getDeleteErrorByLineItem(item.id));
  const error = dismissError || deleteError;
  const theme = useTheme();
  const posthog = usePostHog();
  const {
    userHas
  } = useAbacExt();
  const dispatch = useDispatch();
  useEffect(() => {
    if (error) {
      dispatch(toastsActionCreators.pushed({
        severity: "danger",
        message: error,
        timeout: 10000
      }));
    }
  }, [error]);
  if (!itemState || !showDismissed && itemState.isDismissed) {
    return null;
  }
  const handleToggleDismiss = () => {
    if (itemState.isDismissed) {
      dispatch(insightsActionCreators.restoreLineItemStarted(item.id, _ref3 => {
        let {
          data
        } = _ref3;
        if (data) {
          dispatch(toastsActionCreators.pushed({
            severity: "success",
            message: t("WhatElseTableRow.successfullyRestored", {
              name: item.summary
            }),
            timeout: 10000
          }));
          setItemState(data);
          posthog.capture(`Insights What Else – Restore`, {
            lineItemId: item.id
          });
        }
      }));
    } else {
      dispatch(insightsActionCreators.dismissLineItemStarted(item.id, _ref4 => {
        let {
          data
        } = _ref4;
        if (data) {
          dispatch(toastsActionCreators.pushed({
            severity: "success",
            message: t("WhatElseTableRow.successfullyDismissed", {
              name: item.summary
            }),
            timeout: 10000
          }));
          setItemState(data);
          posthog.capture(`Insights What Else – Dismissed`, {
            lineItemId: item.id
          });
        }
      }));
    }
  };
  const handleDelete = () => {
    dispatch(insightsActionCreators.deleteLineItemStarted(item.id, _ref5 => {
      let {
        data
      } = _ref5;
      if (data) {
        dispatch(toastsActionCreators.pushed({
          severity: "success",
          message: t("WhatElseTableRow.successfullyDeleted", {
            name: item.summary
          }),
          timeout: 10000
        }));
        setItemState(undefined);
        posthog.capture(`Insights Blockers – Deleted`, {
          lineItemId: item.id
        });
      }
    }));
  };
  return /*#__PURE__*/React.createElement(Table.Tr, {
    key: item.id
  }, /*#__PURE__*/React.createElement(Table.Td, null), /*#__PURE__*/React.createElement(Table.Td, null, /*#__PURE__*/React.createElement(Markdown, {
    text: item.context,
    linkColor: "primary"
  })), /*#__PURE__*/React.createElement(Table.Td, null, /*#__PURE__*/React.createElement(Markdown, {
    text: item.summary,
    linkColor: "primary"
  })), /*#__PURE__*/React.createElement(Table.Td, null, /*#__PURE__*/React.createElement(Box, {
    gap: 2
  }, item.owner.map(owner => /*#__PURE__*/React.createElement(Text, {
    key: owner,
    color: "textSecondary"
  }, owner)))), /*#__PURE__*/React.createElement(Table.Td, null, /*#__PURE__*/React.createElement(Text, {
    color: lists.insights.urgency.colors(theme)[item.impact],
    weight: "semiBold"
  }, /*#__PURE__*/React.createElement(ImpactIcon, {
    color: lists.insights.urgency.colors(theme)[item.impact]
  }), lists.insights.urgency.names[item.impact])), /*#__PURE__*/React.createElement(Table.Td, null, /*#__PURE__*/React.createElement(Box, {
    align: "center"
  }, moment(item.observedAt).format("ddd, MMM Do")), item.isClosed && /*#__PURE__*/React.createElement(Box, {
    align: "center",
    s: {
      mt: 1
    }
  }, /*#__PURE__*/React.createElement(Icon, {
    src: "checkboxFill",
    s: {
      mt: -0.5,
      mr: 1
    },
    color: "success"
  }), moment(item.closedAt).format("ddd, MMM Do"))), /*#__PURE__*/React.createElement(Table.Td, null, isEditable && /*#__PURE__*/React.createElement(Dropdown, {
    direction: "bottomLeft",
    handlerProps: {
      s: {
        p: 2
      },
      "data-tooltip-id": "general-tooltip",
      "data-tooltip-content": t("common.actions.options")
    },
    handler: /*#__PURE__*/React.createElement(Icon, {
      src: "dots-vertical",
      color: theme.colors.text.primary,
      display: "block"
    })
  }, _ref6 => {
    let {
      isOpen,
      onItemClick
    } = _ref6;
    return /*#__PURE__*/React.createElement(Dropdown.List, {
      isOpen: isOpen
    }, /*#__PURE__*/React.createElement(Dropdown.ListItem, {
      onClick: e => {
        e.stopPropagation();
        handleToggleDismiss();
        onItemClick();
      }
    }, dismissStatus === StatusStateEnum.LOADING ? /*#__PURE__*/React.createElement(Spinner, {
      scale: 4,
      s: {
        mr: 2
      },
      color: "primary"
    }) : /*#__PURE__*/React.createElement(Icon, {
      src: itemState.isDismissed ? "refresh" : "cross",
      scale: 4.5,
      s: {
        mr: 2
      },
      color: "primary"
    }), itemState.isDismissed ? t("common.actions.restore") : t("common.actions.dismiss"), /*#__PURE__*/React.createElement(Icon, {
      s: {
        ml: 1
      },
      color: "primary",
      "data-tooltip-id": "general-tooltip",
      "data-tooltip-html": itemState.isDismissed ? "<strong>Show</strong> Blocker in your report" : "<strong>Hide</strong> Blocker from your report",
      src: "infoOutline",
      style: {
        cursor: "help"
      }
    })), userHas(AuthoritiesEnum.EXPERIMENTAL) && /*#__PURE__*/React.createElement(Dropdown.ListItem, {
      onClick: e => {
        e.stopPropagation();
        handleDelete();
        onItemClick();
      }
    }, deleteStatus === StatusStateEnum.LOADING ? /*#__PURE__*/React.createElement(Spinner, {
      scale: 4,
      s: {
        mr: 2
      },
      color: "primary"
    }) : /*#__PURE__*/React.createElement(Icon, {
      src: "bin",
      scale: 4.5,
      s: {
        mr: 2
      },
      color: "danger"
    }), t("common.actions.delete"), /*#__PURE__*/React.createElement(Icon, {
      src: "encrypted",
      scale: 4,
      color: "primary",
      s: {
        ml: 1
      }
    })));
  })), /*#__PURE__*/React.createElement(Table.Td, null));
};
export default WhatElseTableRow;